import { Component, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ReportControl } from '@xmedical/core';
import { ReportControlEditorService } from '../../services/report-control-editor.service';

// Import all renderer components
import { SingleLineTextFieldRendererComponent } from '../single-line-text-field-renderer/single-line-text-field-renderer.component';
import { MultiLineTextFieldRendererComponent } from '../multi-line-text-field-renderer/multi-line-text-field-renderer.component';
import { DatePickerRendererComponent } from '../date-picker-renderer/date-picker-renderer.component';
import { CommentFieldRendererComponent } from '../comment-field-renderer/comment-field-renderer.component';
import { SectionTitleRendererComponent } from '../section-title-renderer/section-title-renderer.component';
import { PatientSummaryRendererComponent } from '../patient-summary-renderer/patient-summary-renderer.component';
import { ProcedureFieldRendererComponent } from '../procedure-field-renderer/procedure-field-renderer.component';
import { MedicationListRendererComponent } from '../medication-list-renderer/medication-list-renderer.component';
import { ProblemListRendererComponent } from '../problem-list-renderer/problem-list-renderer.component';
import { DiagnosisListRendererComponent } from '../diagnosis-list-renderer/diagnosis-list-renderer.component';
import { TreatmentPlanListRendererComponent } from '../treatment-plan-list-renderer/treatment-plan-list-renderer.component';
import { BillingIcd10ListRendererComponent } from '../billing-icd10-list-renderer/billing-icd10-list-renderer.component';
import { BillingLineItemsRendererComponent } from '../billing-line-items-renderer/billing-line-items-renderer.component';
import { StickerFieldRendererComponent } from '../sticker-field-renderer/sticker-field-renderer.component';
import { ImageFieldRendererComponent } from '../image-field-renderer/image-field-renderer.component';

/**
 * ReportControlEditorComponent
 *
 * Main orchestrator component that routes to the appropriate renderer
 * based on the control type. This component is used within reports
 * to display and edit individual controls.
 *
 * Acts as the single integration point for the ReportControlEditorService.
 * All child renderers emit valueChanged events which are forwarded to the service here.
 */
@Component({
  selector: 'xmed-report-control-editor',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    SingleLineTextFieldRendererComponent,
    MultiLineTextFieldRendererComponent,
    DatePickerRendererComponent,
    CommentFieldRendererComponent,
    SectionTitleRendererComponent,
    PatientSummaryRendererComponent,
    ProcedureFieldRendererComponent,
    MedicationListRendererComponent,
    ProblemListRendererComponent,
    DiagnosisListRendererComponent,
    TreatmentPlanListRendererComponent,
    BillingIcd10ListRendererComponent,
    BillingLineItemsRendererComponent,
    StickerFieldRendererComponent,
    ImageFieldRendererComponent
  ],
  templateUrl: './report-control-editor.component.html',
  styleUrl: './report-control-editor.component.scss'
})
export class ReportControlEditorComponent {
  private editorService = inject(ReportControlEditorService);

  patientId = input<number>(0);
  control = input.required<ReportControl>();

  // Refresh triggers for data-loading components
  // Parent should increment these when corresponding data changes
  medicationRefreshTrigger = input<number>(0);
  problemRefreshTrigger = input<number>(0);
  diagnosisRefreshTrigger = input<number>(0);
  treatmentPlanRefreshTrigger = input<number>(0);

  deleteControl = output<void>();

  onValueChanged(): void {
    this.editorService.triggerEvent();
  }

  delete(): void {
    this.deleteControl.emit();
  }
}
